import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { IOrderById, IOrderDataAttributes } from '../../models/order.models';
import { OrdersFacade } from '../../facades/orders.facade';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ShopCartActions } from '../../actions';
import { Store } from '@ngrx/store';
import { State } from '../../reducers';
import { PageTypes } from '../../analytics/enums/pageTypes';
import { AnalyticsService } from '../../analytics/analytics.service';
import { EFeatureToggles } from '../../configurations/common';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { OrdersUtils } from '../../utils/orders.utils';

@Component({
  selector: 'app-order-thank-you-page',
  templateUrl: './order-thank-you-page.component.html',
  styleUrls: ['./order-thank-you-page.component.scss'],
  standalone: false,
})
export class OrderThankYouPageComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private translate: TranslateService = inject(TranslateService);
  private ordersFacade: OrdersFacade = inject(OrdersFacade);
  private store: Store<State> = inject<Store<State>>(Store);
  private translateService: TranslateService = inject(TranslateService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  order: any = {
    orderNumber: '',
  };
  orderedItemsAccordion: boolean = false;
  questionEmail: string = '';
  isEligibleForReturn: boolean = false;
  isReturnableDocumentEmailLinkEnable: boolean = false;
  orderData: IOrderDataAttributes | null = null;
  returnDocumentUrl: string = '';
  sparePartsReturnDocumentUrlKey: string = environment.sparePartsReturnDocumentUrlKey;
  isProductDiscontinuedStatusEnabled$: Observable<boolean> = new Observable<boolean>();

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.isProductDiscontinuedStatusEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_DISCONTINUED_STATUS);

    this.subscribeToRouteParams();
    this.getQuestionEmail();


    this.ordersFacade.getOrderById(this.order.orderNumber)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((orderResponseWithMessages: IOrderById) => {
        const orderData: IOrderDataAttributes | null =
          (orderResponseWithMessages.data?.attributes as IOrderDataAttributes) ?? null;
        this.orderData = orderData;
        this.isEligibleForReturn = orderData ? OrdersUtils.isSapOrderEligibleForReturn(orderData) : false;
        this.analyticsService.setProducts(orderData?.items ?? []);
        this.analyticsService.trackPageReady('Order Thank You', PageTypes.ORDER_THANK_YOU_PAGE, 'thank-you-page');
      });
    this.store.dispatch(ShopCartActions.updateCartDataSuccess(this.order.orderNumber));
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.SUBMIT_RETURN_DOCUMENT).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isReturnableDocumentEmailLinkEnable = value;
    });
    this._getReturnDocumentUrl();
  }

  toggleOrderedItemsAccordion(): void {
    this.orderedItemsAccordion = !this.orderedItemsAccordion;
  }

  downloadDocumentation(): void {
    window.open(this.returnDocumentUrl, '_blank');
  }

  getTotalItemTranslationValue(itemsCount: number | undefined): string {
    const value: string = (itemsCount ?? 0) <= 1 ? 'singular' : 'plural';
    return `order-thank-you-page.total-item-number-${value}`;
  }

  private _getReturnDocumentUrl(): void {
    this.translateService.get(this.sparePartsReturnDocumentUrlKey).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.returnDocumentUrl = value;
    });
  }

  private subscribeToRouteParams(): void {
    this.route.params.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(params => {
      if (params.orderId) {
        this.order.orderNumber = params.orderId;
        this.ordersFacade.recallAndClearOrdersWithDetailsDataList();
      }
    });
  }

  private getQuestionEmail(): void {
    this.translate.get('order-thank-you-page.question.email').pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.questionEmail = value;
    });
  }
}
